import { colors } from "../theme";
import { AnimatedStat } from "../components";

interface StatsProps {
  isDark: boolean;
  isVisible: boolean;
}

export function Stats({ isDark, isVisible }: StatsProps) {
  return (
    <section id="efficiency" className="py-32 px-8 relative">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-20">
          <h2
            className={`text-4xl md:text-5xl font-semibold tracking-tight mb-6 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            Pricing aligned with reality
          </h2>
          <p
            className={`text-xl max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            style={{ color: isDark ? colors.base01 : colors.base1 }}
          >
            Archival data should cost less over time—because it costs{" "}
            <em>us</em> less too. Our incentives are aligned with yours. When
            your data sleeps, so does your bill. That's not a pricing trick.
            That's just physics.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-12">
          <AnimatedStat
            value={75}
            suffix="%"
            label="Energy Savings"
            isVisible={isVisible}
            isDark={isDark}
          />
          <AnimatedStat
            value={10}
            suffix="+ PB"
            label="Per Rack"
            isVisible={isVisible}
            isDark={isDark}
          />
          <AnimatedStat
            value={0}
            suffix=""
            label="Egress Surprises"
            isVisible={isVisible}
            isDark={isDark}
          />
        </div>

        {/* Pricing philosophy cards */}
        <div
          className={`mt-24 grid md:grid-cols-2 gap-8 transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div
            className="p-8 rounded-2xl transition-colors duration-[2000ms]"
            style={{
              backgroundColor: isDark ? `${colors.cyan}10` : `${colors.blue}08`,
              border: `1px solid ${isDark ? `${colors.cyan}30` : `${colors.blue}20`}`,
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: isDark ? colors.cyan : colors.blue,
                }}
              />
              <span
                className="font-medium"
                style={{ color: isDark ? colors.cyan : colors.blue }}
              >
                How we work
              </span>
            </div>
            <h3
              className="text-2xl font-semibold mb-3"
              style={{ color: isDark ? colors.base0 : colors.base00 }}
            >
              Costs decrease as data ages
            </h3>
            <p style={{ color: isDark ? colors.base01 : colors.base1 }}>
              Fresh data needs fast access. Old data mostly sleeps. Your pricing
              reflects that reality—not artificial lock-in.
            </p>
          </div>

          <div
            className="p-8 rounded-2xl transition-colors duration-[2000ms]"
            style={{
              backgroundColor: isDark ? colors.base02 : colors.base2,
              border: `1px solid ${isDark ? colors.base01 : colors.base1}20`,
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: isDark ? colors.base01 : colors.base1,
                }}
              />
              <span
                className="font-medium"
                style={{ color: isDark ? colors.base01 : colors.base1 }}
              >
                Trad cloud
              </span>
            </div>
            <h3
              className="text-2xl font-semibold mb-3"
              style={{ color: isDark ? colors.base01 : colors.base1 }}
            >
              Same price forever
            </h3>
            <p style={{ color: isDark ? colors.base01 : colors.base1 }}>
              Pay full price whether data is fresh or 5 years old. DIY your own
              S3-to-Glacier tiering, or pay another vendor to do it for you.
              Egress fees when you actually need your data back. It's giving...
              Stockholm syndrome.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
